import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Cake, Confetti, Trophy, Megaphone, FileText, Link as LinkIcon, Star, UserCircle, Medal, ArrowUp, Calendar, Camera, Plus, Trash, X, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// ===================== CELEBRATIONS =====================
export function CelebrationsCard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/intranet/celebrations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => process.env.NODE_ENV === "development" && console.error(e.message));
  }, [token]);

  if (!data) return null;
  const { birthdays, anniversaries } = data;
  if (birthdays.length === 0 && anniversaries.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="celebrations-card">
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-3">
        <h3 className="text-white font-bold flex items-center gap-2"><Confetti size={20} weight="fill" /> Celebraciones - {MONTHS[(data.month || 1) - 1]}</h3>
      </div>
      <div className="p-5 space-y-4">
        {birthdays.length > 0 && (
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Cake size={14} /> Cumpleanos</h4>
            <div className="space-y-2">
              {birthdays.map(b => (
                <div key={b.id} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${b.is_today ? "bg-amber-50 border border-amber-200 ring-2 ring-amber-300" : "hover:bg-slate-50"}`} data-testid={`birthday-${b.id}`}>
                  {b.photo_url ? (
                    <img src={`${API}/admin/employee-files/${b.photo_url}`} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-amber-300" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Cake size={18} className="text-amber-600" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{b.name}</p>
                    <p className="text-xs text-slate-500">{b.position}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-slate-400">{b.date?.slice(5)}</p>
                    {b.is_today && <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">HOY</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {anniversaries.length > 0 && (
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Star size={14} /> Aniversarios en la Empresa</h4>
            <div className="space-y-2">
              {anniversaries.map(a => (
                <div key={a.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${a.is_today ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50"}`} data-testid={`anniversary-${a.id}`}>
                  {a.photo_url ? (
                    <img src={`${API}/admin/employee-files/${a.photo_url}`} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-blue-300" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Star size={18} className="text-blue-600" weight="fill" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{a.name}</p>
                    <p className="text-xs text-slate-500">{a.position}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-blue-700">{a.years} {a.years === 1 ? "ano" : "anos"}</p>
                    {a.is_today && <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">HOY</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== ANNOUNCEMENTS VIEWER =====================
export function AnnouncementsCard() {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/intranet/announcements`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAnnouncements(r.data || []))
      .catch(e => process.env.NODE_ENV === "development" && console.error(e.message));
  }, [token]);

  if (announcements.length === 0) return null;

  const priorityStyles = {
    urgente: "border-l-red-500 bg-red-50/50",
    importante: "border-l-amber-500 bg-amber-50/30",
    normal: "border-l-blue-500",
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="announcements-card">
      <div className="bg-slate-800 px-5 py-3">
        <h3 className="text-white font-bold flex items-center gap-2"><Megaphone size={20} weight="fill" /> Avisos Importantes</h3>
      </div>
      <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
        {announcements.map(ann => (
          <div key={ann.id} className={`border-l-4 border rounded-lg p-4 ${priorityStyles[ann.priority] || priorityStyles.normal}`} data-testid={`announcement-${ann.id}`}>
            <div className="flex justify-between items-start mb-1">
              <h4 className="text-sm font-bold text-slate-800">{ann.title}</h4>
              {ann.priority === "urgente" && <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full shrink-0">URGENTE</span>}
            </div>
            {ann.content && <p className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">{ann.content}</p>}
            <div className="flex flex-wrap gap-2">
              {ann.google_form_url && (
                <a href={ann.google_form_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 px-2.5 py-1 rounded-full" data-testid={`ann-form-${ann.id}`}>
                  <LinkIcon size={12} /> Politicas y Reglamentos
                </a>
              )}
              {(ann.attachments || []).map(att => (
                <a key={att.id} href={`${API}/intranet/files/${att.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800 bg-slate-100 px-2.5 py-1 rounded-full" data-testid={`ann-file-${att.id}`}>
                  <FileText size={12} /> {att.filename}
                </a>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">{ann.created_by_name} - {ann.created_at?.slice(0, 10)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===================== ATTENDANCE RANKING =====================
export function AttendanceRankingCard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/intranet/ranking`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => process.env.NODE_ENV === "development" && console.error(e.message));
  }, [token]);

  if (!data || data.ranking.length === 0) return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="ranking-card">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3">
        <h3 className="text-white font-bold flex items-center gap-2"><Trophy size={20} weight="fill" /> Ranking Asistencia</h3>
      </div>
      <div className="p-6 text-center text-sm text-slate-400">Sin datos de asistencia este mes</div>
    </div>
  );

  const medals = ["text-amber-500", "text-slate-400", "text-amber-700"];

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="ranking-card">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><Trophy size={20} weight="fill" /> Ranking Asistencia — {MONTHS[(data.month || 1) - 1]}</h3>
        <span className="text-white/80 text-xs font-medium">{data.ranking.length} empleados</span>
      </div>
      <div className="p-4">
        <div className="space-y-1.5">
          {data.ranking.map((r, i) => (
            <div key={r.user_id} className={`flex items-center gap-3 p-2.5 rounded-lg ${i < 3 ? "bg-emerald-50/50 border border-emerald-100" : "hover:bg-slate-50"}`} data-testid={`rank-${i}`}>
              <div className="w-8 text-center">
                {i < 3 ? (
                  <Medal size={22} weight="fill" className={medals[i]} />
                ) : (
                  <span className="text-sm font-bold text-slate-400">{i + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{r.user_name}</p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-700 font-semibold" title="Puntuales">{r.on_time} puntuales</span>
                {r.late > 0 && <span className="text-amber-600">{r.late} retardos</span>}
                {r.absent > 0 && <span className="text-red-500">{r.absent} faltas</span>}
                {r.late === 0 && r.absent === 0 && r.on_time > 0 && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">PERFECTO</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===================== ADMIN: ANNOUNCEMENTS MANAGER =====================
export function AnnouncementsManager() {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", type: "aviso", target_roles: ["todos"], google_form_url: "", priority: "normal" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const ROLE_OPTIONS = [
    { value: "todos", label: "Todos" },
    { value: "admin", label: "Administradores" },
    { value: "ingeniero", label: "Ingenieria" },
    { value: "comercial", label: "Comercial" },
    { value: "auxiliar", label: "Auxiliar" },
    { value: "marketing", label: "Marketing" },
  ];

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/intranet/announcements`, { headers });
      setAnnouncements(data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ title: "", content: "", type: "aviso", target_roles: ["todos"], google_form_url: "", priority: "normal" }); setEditId(null); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Titulo requerido"); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/intranet/announcements/${editId}`, form, { headers });
        toast.success("Aviso actualizado");
      } else {
        await axios.post(`${API}/intranet/announcements`, form, { headers });
        toast.success("Aviso publicado");
      }
      resetForm(); setShowForm(false); load();
    } catch (e) { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const handleEdit = (ann) => {
    setForm({ title: ann.title, content: ann.content || "", type: ann.type || "aviso", target_roles: ann.target_roles || ["todos"], google_form_url: ann.google_form_url || "", priority: ann.priority || "normal" });
    setEditId(ann.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este aviso?")) return;
    try { await axios.delete(`${API}/intranet/announcements/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch (e) { toast.error("Error"); }
  };

  const handleUploadFile = async (annId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/intranet/announcements/${annId}/attachment`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Archivo adjunto subido");
      load();
    } catch (er) { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const toggleRole = (role) => {
    if (role === "todos") {
      setForm({ ...form, target_roles: ["todos"] });
    } else {
      let roles = form.target_roles.filter(r => r !== "todos");
      if (roles.includes(role)) {
        roles = roles.filter(r => r !== role);
      } else {
        roles.push(role);
      }
      setForm({ ...form, target_roles: roles.length === 0 ? ["todos"] : roles });
    }
  };

  return (
    <div className="space-y-4" data-testid="announcements-manager">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Megaphone size={18} /> Gestionar Avisos</h3>
        <Button size="sm" className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-announcement-btn"><Plus size={16} className="mr-1" /> Nuevo Aviso</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="announcement-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Titulo *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="ann-title" /></div>
            <div>
              <Label className="text-xs">Prioridad</Label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full px-2 py-2 border rounded-lg text-sm" data-testid="ann-priority">
                <option value="normal">Normal</option>
                <option value="importante">Importante</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
          <div><Label className="text-xs">Contenido</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="mt-1 min-h-[80px]" data-testid="ann-content" /></div>
          <div><Label className="text-xs">Link Google Form (Politicas/Reglamentos)</Label><Input value={form.google_form_url} onChange={e => setForm({ ...form, google_form_url: e.target.value })} className="mt-1" placeholder="https://forms.gle/..." data-testid="ann-form-url" /></div>
          <div>
            <Label className="text-xs">Dirigido a:</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {ROLE_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => toggleRole(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${form.target_roles.includes(opt.value) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                  data-testid={`ann-role-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="ann-save"><FloppyDisk size={14} className="mr-1" /> {editId ? "Actualizar" : "Publicar"}</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}><X size={14} className="mr-1" /> Cancelar</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {announcements.map(ann => (
          <div key={ann.id} className="bg-white rounded-lg border p-4 flex items-start gap-4" data-testid={`manage-ann-${ann.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-slate-800">{ann.title}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ann.priority === "urgente" ? "bg-red-100 text-red-700" : ann.priority === "importante" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{ann.priority}</span>
                <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded">{(ann.target_roles || []).join(", ")}</span>
              </div>
              {ann.content && <p className="text-xs text-slate-600 line-clamp-2">{ann.content}</p>}
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(ann.attachments || []).map(att => (
                  <span key={att.id} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{att.filename}</span>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">{ann.created_at?.slice(0, 10)}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handleUploadFile(ann.id, e)} className="hidden" id={`ann-upload-${ann.id}`} />
                <label htmlFor={`ann-upload-${ann.id}`} className="cursor-pointer p-1.5 text-slate-400 hover:text-blue-600" title="Adjuntar archivo"><Camera size={16} /></label>
              </div>
              <button onClick={() => handleEdit(ann)} className="p-1.5 text-blue-500 hover:text-blue-700"><FileText size={16} /></button>
              <button onClick={() => handleDelete(ann.id)} className="p-1.5 text-red-400 hover:text-red-600" data-testid={`delete-ann-${ann.id}`}><Trash size={16} /></button>
            </div>
          </div>
        ))}
        {announcements.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Sin avisos publicados</p>}
      </div>
    </div>
  );
}
